import { useSearchParams } from 'react-router-dom';
import { TimeWindowPicker, presetToDateRange } from '../components/TimeWindowPicker';
import { ServiceFilter } from '../components/ServiceFilter';
import { KPICard, KPIGrid } from '../components/KPICards';
import { FunnelChart, FunnelValueTable } from '../components/FunnelChart';
import { LeadMetricsTable } from '../components/LeadMetricsTable';
import { useKommoData } from '../hooks/useKommoData';
import { formatBRL, formatPercent, formatNumber } from '../lib/format';
import type { ServiceLabel, TimeWindow } from '../types';
import { SERVICE_COLORS } from '../types';

export default function Kommo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const preset = (searchParams.get('preset') ?? '30d') as TimeWindow['preset'];
  const service = (searchParams.get('service') ?? 'all') as ServiceLabel;
  const { startDate, endDate } = presetToDateRange(preset);

  function setPreset(p: TimeWindow['preset']) { setSearchParams(sp => { sp.set('preset', p); return sp; }); }
  function setService(s: ServiceLabel) { setSearchParams(sp => { sp.set('service', s); return sp; }); }

  const { funnel, leads, isLoading } = useKommoData({ startDate, endDate, service });
  const funnelColor = SERVICE_COLORS[service];

  return (
    <div style={{ padding: '28px', maxWidth: '1440px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: 'var(--text-primary)' }}>Kommo CRM</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>Funis e leads por serviço</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ServiceFilter value={service} onChange={setService} />
          <TimeWindowPicker value={preset} onChange={setPreset} />
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <div className="spinner" />
        </div>
      ) : (
        <>
          <KPIGrid columns={4}>
            <KPICard label="Total de Leads" value={formatNumber(funnel?.totalLeads ?? 0)} accentColor="var(--green)" delay={0} />
            <KPICard label="Leads Ganhos" value={formatNumber(funnel?.totalWon ?? 0)} delay={0.04} />
            <KPICard label="Taxa de Conversão" value={formatPercent(funnel && funnel.totalLeads > 0 ? (funnel.totalWon / funnel.totalLeads) * 100 : 0)} delay={0.08} />
            <KPICard label="Receita Total" value={formatBRL(funnel?.totalRevenue ?? 0)} accentColor="var(--accent)" delay={0.12} />
          </KPIGrid>

          {/* Funnel Chart */}
          <div style={{ marginTop: '24px' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Funil de Vendas
            </h2>
            <FunnelChart stages={funnel?.stages ?? []} color={funnelColor} />
            <FunnelValueTable stages={funnel?.stages ?? []} />
          </div>

          {/* Leads Table */}
          <div style={{ marginTop: '24px' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Leads
            </h2>
            <LeadMetricsTable leads={leads} />
          </div>
        </>
      )}
    </div>
  );
}
